import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, Lock, Eye, EyeOff, CheckCircle } from "lucide-react";
import VoiceLogo from "@/components/VoiceLogo";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [success, setSuccess] = useState(false);

  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      toast.error("Invalid reset link");
      navigate("/");
    }
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setIsLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      }, { withCredentials: true });
      
      setSuccess(true);
      toast.success("Password reset successful!");
      
      // Redirect after 2 seconds
      setTimeout(() => {
        navigate("/");
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Reset failed. Link may have expired.");
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-6"
        style={{ backgroundColor: '#0f1419' }}
      >
        <div 
          className="w-full max-w-md rounded-2xl p-8 text-center"
          style={{ backgroundColor: '#1a2332', border: '1px solid #2d3748' }}
        >
          <CheckCircle className="h-16 w-16 mx-auto mb-4" style={{ color: '#22c55e' }} />
          <h2 className="text-xl font-bold mb-2" style={{ color: '#f5f5f5' }}>
            Password Reset!
          </h2>
          <p style={{ color: '#a0aec0' }}>
            Redirecting you to the app...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-6"
      style={{ backgroundColor: '#0f1419' }}
    >
      <div 
        className="w-full max-w-md rounded-2xl p-8"
        style={{ backgroundColor: '#1a2332', border: '1px solid #2d3748' }}
      >
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <VoiceLogo />
          <h1 
            className="text-2xl font-bold mt-2"
            style={{ fontFamily: 'Manrope, sans-serif', color: '#f5f5f5' }}
          >
            Reset Password
          </h1>
          <p style={{ color: '#a0aec0' }} className="text-sm mt-1">
            Enter your new password
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: '#718096' }} />
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="New password"
              required
              minLength={6}
              className="w-full pl-10 pr-12 py-3 rounded-xl border-none focus:ring-2 focus:ring-purple-500 focus:outline-none"
              style={{ backgroundColor: '#0f1419', color: '#f5f5f5' }}
              data-testid="new-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2"
              style={{ color: '#718096' }}
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>

          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: '#718096' }} />
            <input
              type={showPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              required
              className="w-full pl-10 pr-4 py-3 rounded-xl border-none focus:ring-2 focus:ring-purple-500 focus:outline-none"
              style={{ backgroundColor: '#0f1419', color: '#f5f5f5' }}
              data-testid="confirm-password-input"
            />
          </div>

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full py-3 rounded-xl text-white font-medium"
            style={{ backgroundColor: '#6366f1' }}
            data-testid="reset-submit-btn"
          >
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              "Reset Password"
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
